/* Navigation model — the sidebar's three groups and the icons for each surface,
   ported from the prototype's TABS + _navIcon(). Paths are the deep-linkable
   routes; `key` ties a surface to its integration/contract source. */
import type { ReactNode } from 'react';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  group: 'surfaces' | 'product';
}

// The dashboard is served under /app (public login card lives at /). Access is
// re-scoped to /app + /api at the edge post-merge; client routing is unaffected.
export const NAV: NavItem[] = [
  { id: 'command', label: 'Command', path: '/app/command', group: 'surfaces' },
  { id: 'plan', label: 'Plan', path: '/app/plan', group: 'surfaces' },
  { id: 'crew', label: 'Crew', path: '/app/crew', group: 'surfaces' },
  { id: 'site', label: 'Site', path: '/app/site', group: 'surfaces' },
  { id: 'code', label: 'Code', path: '/app/code', group: 'surfaces' },
  { id: 'money', label: 'Money', path: '/app/money', group: 'surfaces' },
  { id: 'controls', label: 'Approvals & Actions', path: '/app/controls', group: 'surfaces' },
  { id: 'reviews', label: 'Reviews', path: '/app/reviews', group: 'product' },
  { id: 'performance', label: 'Performance', path: '/app/performance', group: 'product' },
];

export const labelForPath = (path: string): string =>
  NAV.find((n) => n.path === path)?.label ?? 'Command';

const svg = (children: ReactNode, size = 18) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const rect = (x: number, y: number, k: string) => <rect key={k} x={x} y={y} width={7} height={7} rx={1.5} />;

/** Ported from the prototype's _navIcon(id). */
export function NavIcon({ id, size = 18 }: { id: string; size?: number }): ReactNode {
  switch (id) {
    case 'command':
      return svg([rect(3, 3, 'a'), rect(14, 3, 'b'), rect(3, 14, 'c'), rect(14, 14, 'd')], size);
    case 'plan':
      // Inbox tray — the founder brain-dump + project queue surface.
      return svg(<>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" />
        <path d="M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.7 4H7.3a2 2 0 0 0-1.8 1.1Z" />
      </>, size);
    case 'crew':
      return svg(<>
        <circle cx={9} cy={8} r={3} />
        <path d="M3 20a6 6 0 0 1 12 0M17 11a3 3 0 1 0 0-6M22 20a6 6 0 0 0-3.5-5.5" />
      </>, size);
    case 'site':
      return svg(<>
        <circle cx={12} cy={12} r={9} />
        <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
      </>, size);
    case 'code':
      return svg(<path d="m16 18 6-6-6-6M8 6l-6 6 6 6" />, size);
    case 'money':
      return svg(<path d="M12 2v20M17 6.5C17 4.6 14.8 3.5 12 3.5S7 4.8 7 7s2 3 5 3.5 5 1.3 5 3.5-2.2 3.5-5 3.5-5-1.1-5-3" />, size);
    case 'controls':
      return svg(<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />, size);
    case 'reviews':
      return svg(<path d="M11.5 3.2a.55.55 0 0 1 1 0l2.1 4.3 4.7.7a.55.55 0 0 1 .3.94l-3.4 3.3.8 4.7a.55.55 0 0 1-.8.58L12 15.6l-4.2 2.2a.55.55 0 0 1-.8-.58l.8-4.7-3.4-3.3a.55.55 0 0 1 .3-.94l4.7-.7z" />, size);
    case 'performance':
      return svg(<path d="M3 3v18h18M18 17V9M13 17V6M8 17v-4" />, size);
    default:
      return svg(<circle cx={12} cy={12} r={9} />, size);
  }
}
